import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Upload, FileText, Loader2, AlertCircle, Check } from "lucide-react";
import { accountApi } from "../lib/api";

type ExtractedContext = {
  simulatorId: string;
  simulatorName: string;
  nameFr: string;
  nameEn: string;
  descriptionFr?: string;
  descriptionEn?: string;
  _tempId: string;
  _selected: boolean;
};

type Simulator = { id: string; name: string };

type Props = {
  open: boolean;
  onClose: () => void;
  accountId?: string;
  simulators: Simulator[];
  onCreated: (count: number) => void;
};

const ACCEPT_FILES = ".pdf,.docx,.xlsx,.xls,.csv,.txt,.md";
const MAX_FILE_SIZE = 10 * 1024 * 1024;

async function fileToBase64(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
  return btoa(bin);
}

function detectTypeFromFilename(name: string): "pdf" | "docx" | "xlsx" | "csv" | "text" | null {
  const lower = name.toLowerCase();
  if (lower.endsWith(".pdf")) return "pdf";
  if (lower.endsWith(".docx") || lower.endsWith(".doc")) return "docx";
  if (lower.endsWith(".xlsx") || lower.endsWith(".xls")) return "xlsx";
  if (lower.endsWith(".csv")) return "csv";
  if (lower.endsWith(".txt") || lower.endsWith(".md")) return "text";
  return null;
}

export function ContextsImportModal({ open, onClose, accountId, simulators, onCreated }: Props) {
  const { t } = useTranslation();
  const [step, setStep] = useState<"input" | "extracting" | "review">("input");
  const [pasteText, setPasteText] = useState("");
  const [fileName, setFileName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [extracted, setExtracted] = useState<ExtractedContext[]>([]);
  const [creating, setCreating] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setStep("input");
    setPasteText("");
    setFileName(null);
    setError(null);
    setExtracted([]);
    setCreating(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const processFile = async (file: File) => {
    if (file.size > MAX_FILE_SIZE) {
      setError("Fichier trop volumineux (max 10 Mo).");
      return;
    }
    const type = detectTypeFromFilename(file.name);
    if (!type) {
      setError("Format non supporté. Utilisez PDF, DOCX, XLSX, CSV, TXT ou MD.");
      return;
    }
    setError(null);
    setFileName(file.name);
    setStep("extracting");
    try {
      let payload: { type: "text" | "pdf" | "docx" | "xlsx" | "csv"; content: string };
      if (type === "text") {
        const text = await file.text();
        payload = { type: "text", content: text };
      } else if (type === "csv") {
        const text = await file.text();
        payload = { type: "csv", content: text };
      } else {
        const content = await fileToBase64(file);
        payload = { type, content };
      }
      await runExtraction(payload);
    } catch (err: any) {
      setError(err.message || "Erreur lors de la lecture du fichier.");
      setStep("input");
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await processFile(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };
  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    await processFile(file);
  };

  const handlePasteSubmit = async () => {
    if (!pasteText.trim()) {
      setError("Collez votre texte avant de lancer l'extraction.");
      return;
    }
    setError(null);
    setStep("extracting");
    await runExtraction({ type: "text", content: pasteText });
  };

  const runExtraction = async (payload: { type: "text" | "pdf" | "docx" | "xlsx" | "csv"; content: string }) => {
    try {
      const result = await accountApi.extractContextsFromDocument(payload);
      if (!result.extracted || result.extracted.length === 0) {
        setError("Aucun contexte exploitable n'a pu être extrait. Essayez un autre document ou reformulez votre texte.");
        setStep("input");
        return;
      }
      const enriched: ExtractedContext[] = result.extracted.map((c: any, idx: number) => ({
        ...c,
        _tempId: `t_${idx}`,
        _selected: true,
      }));
      setExtracted(enriched);
      setStep("review");
    } catch (err: any) {
      setError(err.message || "Erreur lors de l'extraction.");
      setStep("input");
    }
  };

  const updateItem = (tempId: string, patch: Partial<ExtractedContext>) => {
    setExtracted((prev) => prev.map((c) => (c._tempId === tempId ? { ...c, ...patch } : c)));
  };

  const createAll = async () => {
    const selected = extracted.filter((c) => c._selected && (c.nameFr || c.nameEn) && c.simulatorId);
    if (selected.length === 0) {
      alert(t("contexts.importSelectAtLeastOne"));
      return;
    }
    setCreating(true);
    let created = 0;
    let failed = 0;
    for (const c of selected) {
      try {
        await accountApi.createContext({
          simulatorId: c.simulatorId,
          // Idem ContextsTab : ne pas recopier une langue dans l'autre, sinon la
          // traduction auto ne complète plus la langue manquante (elle skip si les
          // deux colonnes sont non-vides). Le filtre `selected` garantit ≥1 nom.
          nameFr: c.nameFr || "",
          nameEn: c.nameEn || "",
          descriptionFr: c.descriptionFr,
          descriptionEn: c.descriptionEn,
        }, accountId);
        created++;
      } catch {
        failed++;
      }
    }
    setCreating(false);
    if (failed > 0) {
      alert(`${created} contexte(s) créé(s), ${failed} en échec (doublon ou erreur).`);
    }
    onCreated(created);
    handleClose();
  };

  const grouped = extracted.reduce<Record<string, ExtractedContext[]>>((acc, c) => {
    const key = c.simulatorId;
    if (!acc[key]) acc[key] = [];
    acc[key]!.push(c);
    return acc;
  }, {});
  const selectedCount = extracted.filter((c) => c._selected).length;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Importer des contextes depuis un document</DialogTitle>
          <DialogDescription>
            L'IA analyse votre document, extrait les contextes de simulation et les catégorise par simulateur. Vous relisez avant création.
            <span className="block mt-1 text-amber-700">
              <AlertCircle className="inline h-3.5 w-3.5 mr-1" />
              Votre fichier n'est <strong>pas stocké</strong> : il est lu une seule fois pour l'extraction puis supprimé.
            </span>
          </DialogDescription>
        </DialogHeader>

        {step === "input" && (
          <div className="space-y-4">
            {error && (
              <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">{error}</div>
            )}

            <div
              className={`rounded-lg border-2 border-dashed p-6 text-center transition-colors ${dragOver ? "border-[#384872] bg-blue-50" : "border-slate-300"}`}
              onDragOver={handleDragOver}
              onDragEnter={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <Upload className={`h-8 w-8 mx-auto mb-2 ${dragOver ? "text-[#384872]" : "text-slate-400"}`} />
              <p className="text-sm font-medium">{t("contexts.importDropFile")}</p>
              <p className="text-xs text-muted-foreground mb-3">ou cliquez sur le bouton ci-dessous — PDF, Word (.docx), Excel (.xlsx), CSV, TXT, MD — 10 Mo max</p>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT_FILES}
                onChange={handleFileChange}
                className="hidden"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className="border-[#384872] text-[#384872]"
              >
                Choisir un fichier
              </Button>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">ou</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Coller un texte</Label>
              <textarea
                className="w-full min-h-[160px] px-3 py-2 rounded-md border border-input bg-background text-sm resize-y font-mono"
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                placeholder={"Exemple :\n\nContexte 1 : Suite à une démonstration produit — Le prospect a vu une démo complète et cet appel vise à argumenter sur ses besoins.\nContexte 2 : Premier RDV après cold call — Le prospect a accepté un RDV suite à un appel à froid.\n..."}
              />
              <div className="flex justify-end">
                <Button size="sm" onClick={handlePasteSubmit} className="bg-[#384872] hover:bg-[#2d3a5c]">
                  <FileText className="h-4 w-4 mr-2" /> Lancer l'extraction
                </Button>
              </div>
            </div>
          </div>
        )}

        {step === "extracting" && (
          <div className="py-12 text-center">
            <Loader2 className="h-10 w-10 mx-auto animate-spin text-[#384872] mb-3" />
            <p className="text-sm font-medium">L'IA analyse le contenu…</p>
            {fileName && <p className="text-xs text-muted-foreground mt-1">{fileName}</p>}
            <p className="text-xs text-muted-foreground mt-2">{t("contexts.importExtractDuration")}</p>
          </div>
        )}

        {step === "review" && (
          <div className="space-y-4">
            <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-xs text-[#384872]">
              <Check className="inline h-4 w-4 mr-1" />
              <strong>{extracted.length} contexte(s)</strong> extrait(s). Relisez, modifiez si besoin, décochez ce que vous ne voulez pas créer. L'IA a catégorisé chaque contexte par simulateur — vous pouvez changer.
            </div>

            {Object.entries(grouped).map(([simId, items]) => {
              const simName = items[0]?.simulatorName || "Inconnu";
              return (
                <div key={simId} className="rounded-lg border border-slate-200 overflow-hidden">
                  <div className="bg-slate-50 px-3 py-2 border-b border-slate-200 flex items-center justify-between">
                    <span className="text-sm font-semibold text-[#384872]">{simName}</span>
                    <Badge variant="outline" className="text-xs">{items.length} contexte(s)</Badge>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {items.map((c) => (
                      <div key={c._tempId} className="p-3 space-y-2">
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={c._selected}
                            onChange={(e) => updateItem(c._tempId, { _selected: e.target.checked })}
                            className="mt-1 h-4 w-4 rounded border-slate-300"
                          />
                          <div className="flex-1 space-y-2">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              <Input
                                value={c.nameFr}
                                onChange={(e) => updateItem(c._tempId, { nameFr: e.target.value })}
                                placeholder="Nom FR"
                                className="text-sm"
                              />
                              <Input
                                value={c.nameEn}
                                onChange={(e) => updateItem(c._tempId, { nameEn: e.target.value })}
                                placeholder="Nom EN"
                                className="text-sm"
                              />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              <textarea
                                value={c.descriptionFr || ""}
                                onChange={(e) => updateItem(c._tempId, { descriptionFr: e.target.value })}
                                placeholder="Description FR"
                                className="min-h-[60px] px-2 py-1 rounded-md border border-input bg-background text-xs resize-y"
                              />
                              <textarea
                                value={c.descriptionEn || ""}
                                onChange={(e) => updateItem(c._tempId, { descriptionEn: e.target.value })}
                                placeholder="Description EN"
                                className="min-h-[60px] px-2 py-1 rounded-md border border-input bg-background text-xs resize-y"
                              />
                            </div>
                            <div className="flex gap-2 items-center flex-wrap">
                              <Label className="text-xs text-muted-foreground">Simulateur :</Label>
                              <Select
                                value={c.simulatorId}
                                onValueChange={(v) => {
                                  const sim = simulators.find((s) => s.id === v);
                                  updateItem(c._tempId, { simulatorId: v, simulatorName: sim?.name || v });
                                }}
                              >
                                <SelectTrigger className="h-7 text-xs w-auto min-w-[160px]">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {simulators.map((s) => (
                                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            <div className="flex justify-between items-center pt-3 border-t">
              <span className="text-sm text-muted-foreground">
                <strong>{selectedCount}</strong> contexte(s) sélectionné(s) sur {extracted.length}
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setStep("input")} disabled={creating}>
                  Recommencer
                </Button>
                <Button
                  size="sm"
                  className="bg-[#384872] hover:bg-[#2d3a5c]"
                  onClick={createAll}
                  disabled={creating || selectedCount === 0}
                >
                  {creating ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Création…</>) : t("contexts.importCreateCount", { count: selectedCount })}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
